import _ from 'lodash';
import { createSelector } from 'reselect';

import { DEFAULT_DESIGN_PANEL_WIDTH, PILGRIM } from '../constants/report-model-designer.constants';
import { difference } from '../utils/report-model-designer-utils';

export const getReportModelViews = (state) => state.reportModelDesigner?.reportModel?.views ?? [];

export const getReportModelOriginalViews = (state) =>
  state.reportModelDesigner?.reportModel?.originalViews ?? [];

export const getReportModelInfo = (state) => state.reportModelDesigner?.reportModel?.info ?? {};

export const getReportModelName = (state) =>
  state.reportModelDesigner?.reportModel?.info.ReportModelName ?? '';

export const getDataModel = (state) => state.reportModelDesigner?.dataModel ?? null;

export const getFieldLists = (state) => state.reportModelDesigner?.fieldLists ?? {};

export const getSelectedViewId = (state) => state.reportModelDesigner?.selectedViewId ?? null;

export const getDesignPanelWidth = (state) =>
  state.reportModelDesigner?.designPanelWidth ?? DEFAULT_DESIGN_PANEL_WIDTH;

const isNewReportModel = (state) => state.reportModelDesigner?.isNewReportModel || false;

export const isImportedReportModel = (state) =>
  state.reportModelDesigner?.isImportedReportModel || false;

const getReportModelReleaseType = (state) => state.reportModelDesigner?.releaseType;

export const isReportModelExists = (state) =>
  state.reportModelDesigner?.isReportModelExists || false;

export const getIsDesignPanelOpened = (state) =>
  state.reportModelDesigner?.isDesignPanelOpened ?? false;

export const getRootView = createSelector([getReportModelViews], (views) =>
  views.find((v) => !v.parentId)
);

const getDeletedViews = createSelector(
  [getReportModelViews, getReportModelOriginalViews],
  (views, originalViews) => originalViews.filter((view) => !views.some(({ Id }) => view.Id === Id))
);

const getAddedViews = createSelector(
  [getReportModelViews, getReportModelOriginalViews],
  (views, originalViews) => views.filter((view) => !originalViews.some(({ Id }) => view.Id === Id))
);

const getChanges = createSelector(
  [getReportModelViews, getReportModelOriginalViews],
  (views, originalViews) =>
    difference(
      views.map((v) => ({ ...v, Alias: '' })),
      originalViews.map((o) => ({ ...o, Alias: '' }))
    )
);

export const hasUnsavedChanges = createSelector(
  [getChanges, getAddedViews, getDeletedViews],
  (changes, addedViews, deletedViews) =>
    !(_.isEmpty(changes) && _.isEmpty(addedViews) && _.isEmpty(deletedViews))
);

export const isSaveDisabled = createSelector(
  [hasUnsavedChanges, isNewReportModel, isImportedReportModel, isReportModelExists],
  (hasChanges, isNewReportModel, isImported, isExists) =>
    (!hasChanges && !isNewReportModel && !isImported) || (isImported && !isExists)
);

export const isDeleteDisabled = createSelector(
  [getReportModelReleaseType, isNewReportModel, isImportedReportModel],
  (releaseType, isNewReportModel, isImportedReportModel) =>
    releaseType === PILGRIM || isNewReportModel || isImportedReportModel
);

export const isExportDisabled = createSelector(
  [isNewReportModel, isImportedReportModel],
  (isNewReportModel, isImportedReportModel) => isImportedReportModel || isNewReportModel
);

export const hasFetchError = (state) => state.errors.some((error) => error?.hasFetchError);

export const getUserActionPointer = (state) =>
  state.reportModelDesigner?.userActions?.actionPointer ?? 0;

export const getUserActionsArray = (state) => state.reportModelDesigner?.userActions?.actions ?? [];

export const isUndoDisabled = createSelector(
  [getUserActionPointer, getUserActionsArray],
  (actionPointer, actions) => actionPointer === actions?.length - 1
);

export const isRedoDisabled = createSelector(
  [getUserActionPointer],
  (actionPointer) => actionPointer === 0
);
